/* ============================================================
   orderPayView — SD-3 (d36195ae): pure view model for the Safe
   Deal pay surface on the customer order detail (spec §13 SD-3).
   Everything renders from the backend-derived payment state —
   the web never hardcodes fee math, amounts to pay or rail
   enablement (rail off ⇒ paymentState null ⇒ the whole surface
   stays hidden and the order detail renders exactly as before).
   SD-5 (TASK-20ad2261): the refunded state (cancel/dispute full
   refund landed) renders a «money returned» confirmation.
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { SafeDealOrderPaymentState } from '../types/product.ts';
import { formatRub } from '../types/product.ts';

export type OrderPayCueTone = 'accent' | 'success';

export interface OrderPayCue {
  label: string;
  tone: OrderPayCueTone;
}

export interface OrderPayCard {
  tone: 'accent' | 'success';
  title: string;
  text: string;
  /** null on the paid card — no action once the escrow hold exists. */
  ctaLabel: string | null;
}

export interface OrderPayView {
  kind: SafeDealOrderPaymentState;
  cue: OrderPayCue;
  card: OrderPayCard;
}

export interface OrderPayViewInput {
  /** Backend OrderPaymentState — null while the rail is off or no payment applies. */
  paymentState: SafeDealOrderPaymentState | null | undefined;
  /** Order total for the CTA label (display only — the amount charged comes from the backend split). */
  totalPriceRub: number;
}

export function buildOrderPayView(input: OrderPayViewInput): OrderPayView | null {
  if (input.paymentState !== 'awaiting_payment'
      && input.paymentState !== 'paid'
      && input.paymentState !== 'refunded') {
    return null;
  }

  // SD-5 (TASK-20ad2261): the refund leg returned the money (RefundedAtUtc
  // stamp) — a «money back» confirmation replaces the escrow-hold card; no
  // CTA, nothing to retry.
  if (input.paymentState === 'refunded') {
    return {
      kind: 'refunded',
      cue: { label: 'Возвращено', tone: 'success' },
      card: {
        tone: 'success',
        title: 'Деньги возвращены',
        text: 'Оплата по заказу полностью возвращена на карту покупателя. Обычно деньги приходят в течение нескольких дней.',
        ctaLabel: null,
      },
    };
  }

  if (input.paymentState === 'paid') {
    return {
      kind: 'paid',
      cue: { label: 'Оплачен', tone: 'success' },
      card: {
        tone: 'success',
        title: 'Заказ оплачен',
        text: 'Деньги в безопасном удержании — исполнитель получит их после завершения заказа.',
        ctaLabel: null,
      },
    };
  }

  return {
    kind: 'awaiting_payment',
    cue: { label: 'Ожидает оплаты', tone: 'accent' },
    card: {
      tone: 'accent',
      title: 'Ожидает оплаты',
      text: 'Оплата через безопасный платёж: деньги удерживаются сервисом до завершения заказа. Если платёж не удалось завершить — попробуйте ещё раз.',
      ctaLabel: `Оплатить ${formatRub(input.totalPriceRub)}`,
    },
  };
}
